import React from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { v4 as uuidv4 } from 'uuid';

// Define website reference interface
export interface WebsiteReference {
  id: string;
  url: string;
  description: string;
}

interface WebsiteReferenceBuilderProps {
  value: WebsiteReference[];
  onChange: (references: WebsiteReference[]) => void;
  placeholder?: string;
  buttonText?: string;
}

function WebsiteReferenceBuilder(props: WebsiteReferenceBuilderProps) {
  const { 
    value = [], 
    onChange, 
    placeholder = "Briefly describe what you like about this website",
    buttonText = "Add Website"
  } = props;
  const [expandedRefs, setExpandedRefs] = React.useState<string[]>([]);

  // Add a new website reference
  const addWebsiteReference = () => {
    const newRef: WebsiteReference = {
      id: uuidv4(),
      url: '',
      description: ''
    };
    
    onChange([...value, newRef]);
    
    // Auto-expand the newly added reference
    setExpandedRefs(prev => [...prev, newRef.id]);
  };

  // Delete a website reference
  const deleteWebsiteReference = (refId: string) => {
    const updatedRefs = value.filter(ref => ref.id !== refId);
    onChange(updatedRefs);
    
    // Remove from expanded refs
    setExpandedRefs(prev => prev.filter(id => id !== refId));
  };

  // Update website reference url or description
  const updateWebsiteReference = (refId: string, field: 'url' | 'description', newValue: string) => {
    const updatedRefs = value.map(ref => 
      ref.id === refId ? { ...ref, [field]: newValue } : ref
    );
    onChange(updatedRefs);
  };

  // Toggle reference expansion
  const toggleRefExpansion = (refId: string) => {
    setExpandedRefs(prev => {
      if (prev.includes(refId)) {
        return prev.filter(id => id !== refId);
      } else {
        return [...prev, refId];
      }
    });
  };

  // Check if a reference is expanded
  const isRefExpanded = (refId: string) => {
    return expandedRefs.includes(refId);
  };

  return (
    <div className="space-y-4">
      {Array.isArray(value) && value.map(ref => (
        <div key={ref.id} className="border rounded-md p-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">{ref.url || 'New Website'}</div>
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => toggleRefExpansion(ref.id)}
              >
                {isRefExpanded(ref.id) ? 'Collapse' : 'Edit'}
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={() => deleteWebsiteReference(ref.id)}
              >
                Remove
              </Button>
            </div>
          </div>
          
          {isRefExpanded(ref.id) && (
            <div className="space-y-3 mt-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Website URL</label>
                <Input 
                  value={ref.url} 
                  onChange={(e) => updateWebsiteReference(ref.id, 'url', e.target.value)} 
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium">Description</label>
                <textarea
                  value={ref.description} 
                  onChange={(e) => updateWebsiteReference(ref.id, 'description', e.target.value)} 
                  placeholder={placeholder}
                  className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>
          )}
        </div>
      ))}
      
      <Button 
        className="w-full" 
        variant="outline"
        onClick={addWebsiteReference}
      >
        {buttonText}
      </Button>
    </div>
  );
}

export default WebsiteReferenceBuilder; 